import momentJalaali from 'moment-jalaali';
import { ChartLineDataItem } from 'src/app/shared/chart/chart.models';
import { VehicleFingerprintContext, VehicleFingerprintDashboard, VehicleFingerprintSummary } from './vehicle-fingerprint.models';

export function formatFingerprintMetric(
  summary: VehicleFingerprintSummary | null,
  field: keyof VehicleFingerprintSummary,
  decimals: number,
  unit = '',
): string {
  const value = summary?.[field];
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  const formatted = value.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  return unit ? `${formatted} ${unit}` : formatted;
}

export function getFingerprintDates(dashboard: VehicleFingerprintDashboard): string[] {
  const qualityDates = extractDailyDates(Array.isArray(dashboard.Table5) ? dashboard.Table5 : []);
  if (qualityDates.length) return qualityDates;
  return extractDailyDates([dashboard.Table1, dashboard.Table2, dashboard.Table3, dashboard.Table4]
    .filter(Array.isArray).flat());
}

function extractDailyDates(rows: readonly { dtDate: string }[]): string[] {
  const dates = rows.map(row => typeof row?.dtDate === 'string' ? row.dtDate.slice(0, 10) : '')
    .filter(date => momentJalaali.utc(date, 'YYYY-MM-DD', true).isValid());
  return Array.from(new Set(dates)).sort();
}

export function getFingerprintDayCounts(context: VehicleFingerprintContext) {
  const dates = context.dates;
  const qualityByDate = new Map((context.dailyQuality || [])
    .filter(row => typeof row?.dtDate === 'string')
    .map(row => [row.dtDate.slice(0, 10), row] as const));
  const rows = dates?.map(date => qualityByDate.get(date));
  const count = (field: 'bEligibleForBaseline' | 'bEligibleElectrical' | 'bInsideBaselineWindow'): number | null => {
    if (!rows) return null;
    const flags: unknown[] = rows.map(row => row?.[field]);
    if (flags.some(flag => flag !== true && flag !== false && flag !== 1 && flag !== 0)) return null;
    return flags.filter(flag => flag === true || flag === 1).length;
  };
  return {
    totalDays: dates?.length ?? null,
    eligibleDays: count('bEligibleForBaseline'),
    electricalDays: count('bEligibleElectrical'),
    insideBaselineDays: count('bInsideBaselineWindow'),
  };
}

export function buildDailyChartData<T extends { dtDate: string }>(
  dailyRows: readonly T[],
  dates: readonly string[],
  getValue: (row: T) => number | null,
): ChartLineDataItem[] {
  const rows = new Map(dailyRows
    .filter(row => typeof row?.dtDate === 'string')
    .map(row => [row.dtDate.slice(0, 10), row] as const));
  return dates.map(date => {
    const row = rows.get(date);
    return {
      name: momentJalaali.utc(date, 'YYYY-MM-DD', true).locale('en').format('jYYYY/jMM/jDD'),
      value: row ? getValue(row) : null,
    };
  });
}
